import EventEmitter from 'events';
import { z } from 'zod';
import * as Sentry from '@sentry/react';
import { getLocalStorage, getSessionStorage } from '@trinity/chat-components';
import { config } from '@/config';
import { MyInsightsOnline, type MyInsights } from '@/lib';
import type { AuthSource, AuthProviderEvent, AuthenticateJWT } from './auth-provider.base';
import { BaseAuthProvider } from './auth-provider.base';

export class VeevaAuthProvider extends BaseAuthProvider {
  static #tokenKey = 'msl-copilot:veeva:token';
  static #authAttemptsKey = 'msl-copilot:veeva:auth-attempts';

  #eventEmitter: EventEmitter;
  #myInsights: MyInsights;

  #refreshInterval: NodeJS.Timeout;
  #isRefreshingToken: boolean;
  #isAuthenticated: boolean;
  #expiry: Date;

  constructor() {
    super();
    this.#isAuthenticated = false;
    this.#eventEmitter = new EventEmitter();
    this.#myInsights = new MyInsightsOnline(config().VEEVA_AUTH_PROVIDER);
  }

  get isAuthenticated(): boolean {
    return this.#isAuthenticated && this.#expiry != null && this.#expiry > new Date();
  }

  get expiry(): Date {
    return this.#expiry;
  }

  async authenticate(_: AuthenticateJWT) {
    return Sentry.startSpan({ name: 'Veeva Authenticate', op: 'veeva-auth.authenticate' }, async () => {
      try {
        const previousIsAuthenticated = this.isAuthenticated;

        const token = await this.#myInsights.getSSOToken();
        const parsedToken = this.#decodeToken(token);
        this.#storeToken({ token });
        this.#expiry = parsedToken.exp;
        this.#isAuthenticated = true;

        if (this.#refreshInterval) clearInterval(this.#refreshInterval);
        this.#scheduleRefresh(parsedToken.exp);

        if (!previousIsAuthenticated) {
          this.#emit({ name: 'auth/login' });
        }
      } catch (e) {
        await this.error(e);
        await this.logout();
      }
    });
  }

  async error(error: Error) {
    Sentry.captureException(error);
    this.#emit({ name: 'auth/error', error });
  }

  disabled(): void {
    this.#emit({ name: 'auth/disabled' });
  }

  decodePingToken(token: string) {
    return Sentry.startSpan({ name: 'Veeva Decode Ping Token', op: 'veeva-auth.decodePingToken' }, () => {
      const [, body] = token.split('.');
      const parsed = PingParsedJWT.safeParse(JSON.parse(atob(body)));
      if (!parsed.success)
        throw new Error(`Could not parse decoded token: ${JSON.stringify(parsed.error, null, 2)}`);
      return parsed.data;
    });
  }

  decodeEntraToken(token: string) {
    return Sentry.startSpan({ name: 'Veeva Decode Entra Token', op: 'veeva-auth.decodeEntraToken' }, () => {
      const [, body] = token.split('.');
      const parsed = EntraParsedJWT.safeParse(JSON.parse(atob(body)));
      if (!parsed.success)
        throw new Error(`Could not parse decoded token: ${JSON.stringify(parsed.error, null, 2)}`);
      return parsed.data;
    });
  }

  #decodeToken(token: string) {
    return Sentry.startSpan({ name: 'Veeva Decode Token', op: 'veeva-auth.decodeToken' }, () => {
      const provider = config().VEEVA_AUTH_PROVIDER;
      if (provider === 'ping') {
        return this.decodePingToken(token);
      }
      if (provider === 'entra') {
        return this.decodeEntraToken(token);
      }
      throw new Error(`Unsupported VEEVA_AUTH_PROVIDER: ${provider}`);
    });
  }

  #scheduleRefresh(expiry: Date): void {
    if (this.#refreshInterval) clearTimeout(this.#refreshInterval);

    const ttlMs = expiry.getTime() - Date.now();
    const refreshMs = this.#getRefreshDelayMs(ttlMs);

    this.#refreshInterval = setTimeout(() => {
      this.refreshToken().catch(() => undefined);
    }, refreshMs);
  }

  #getRefreshDelayMs(ttlMs: number): number {
    if (ttlMs <= 0) return 0;
    if (ttlMs <= 2_000) return ttlMs - 1_000;

    const desiredRefreshMs = Math.floor(ttlMs * 0.8);
    const latestSafeRefreshMs = ttlMs - 1_000;

    return Math.max(Math.min(desiredRefreshMs, latestSafeRefreshMs), 1_000);
  }

  get authSource(): AuthSource {
    return 'jwt';
  }

  async refreshToken() {
    return Sentry.startSpan({ name: 'Veeva Refresh Token', op: 'veeva-auth.refreshToken' }, async () => {
      if (this.#isRefreshingToken) return;
      try {
        this.#isRefreshingToken = true;

        const tokenData = this.#getToken();
        if (!tokenData?.token) {
          Sentry.captureException(new Error('No token found for refresh'));
          return;
        }
        const expiredToken = tokenData.token;

        const token = await this.#myInsights.refreshSSOToken(expiredToken);

        const parsedToken = this.#decodeToken(token);
        this.#storeToken({ token });
        this.#expiry = parsedToken.exp;
        this.#isAuthenticated = true;
        this.#isRefreshingToken = false;

        if (this.#refreshInterval) clearInterval(this.#refreshInterval);
        this.#scheduleRefresh(parsedToken.exp);
      } catch (e) {
        this.#isRefreshingToken = false;
        await this.error(e);
        await this.logout();
      }
    });
  }

  get isRefreshingToken() {
    return this.#isRefreshingToken;
  }

  get accessToken() {
    return Sentry.startSpan({ name: 'Veeva Get Access Token', op: 'veeva-auth.accessToken' }, () => {
      const data = this.#getToken();
      if (!data) return null;
      return data.token;
    });
  }

  get authAttempts() {
    const $session = getSessionStorage();
    return +($session.getItem(VeevaAuthProvider.#authAttemptsKey) || '0');
  }

  incrementAuthAttempts() {
    const $session = getSessionStorage();
    $session.setItem(VeevaAuthProvider.#authAttemptsKey, `${this.authAttempts + 1}`);
  }

  clearAuthAttempts() {
    const $session = getSessionStorage();
    $session.removeItem(VeevaAuthProvider.#authAttemptsKey);
  }

  logout() {
    return Sentry.startSpan({ name: 'Veeva Logout', op: 'veeva-auth.logout' }, () => {
      const previousIsAuthenticated = this.isAuthenticated;

      this.#isAuthenticated = false;
      this.#expiry = null;
      this.#destroy();

      if (previousIsAuthenticated) {
        this.#emit({ name: 'auth/logout' });
      }
    });
  }

  #emit(e: AuthProviderEvent) {
    return this.#eventEmitter.emit('auth', e);
  }

  addListener(listener: (e: AuthProviderEvent) => void) {
    this.#eventEmitter.addListener('auth', listener);
  }
  removeListener(listener: (e: AuthProviderEvent) => void) {
    this.#eventEmitter.removeListener('auth', listener);
  }

  #storeToken(token: TokenData) {
    return Sentry.startSpan({ name: 'Veeva Store Token', op: 'veeva-auth.storeToken' }, () => {
      const $storage = getLocalStorage();
      $storage.setItem(VeevaAuthProvider.#tokenKey, JSON.stringify(TokenData.parse(token)));
    });
  }

  #getToken() {
    return Sentry.startSpan({ name: 'Veeva Get Token', op: 'veeva-auth.getToken' }, () => {
      const $storage = getLocalStorage();
      try {
        const tokenData = $storage.getItem(VeevaAuthProvider.#tokenKey);
        if (!tokenData) {
          Sentry.captureException(new Error('Veeva token not found in local storage'));
          return null;
        }
        return TokenData.parse(JSON.parse(tokenData));
      } catch {
        Sentry.captureException(new Error('Failed to parse Veeva token from local storage'));
        $storage.removeItem(VeevaAuthProvider.#tokenKey);
        return null;
      }
    });
  }

  #destroy() {
    return Sentry.startSpan({ name: 'Veeva Destroy', op: 'veeva-auth.destroy' }, () => {
      try {
        const $storage = getLocalStorage();
        $storage.removeItem(VeevaAuthProvider.#tokenKey);
      } catch (error) {
        Sentry.captureException(error);
      }
    });
  }
}

const TokenData = z.object({
  token: z.string(),
});
type TokenData = z.infer<typeof TokenData>;

const EntraParsedJWT = z.object({
  upn: z.string(),
  exp: z.coerce.number().transform((u) => new Date(u * 1000)),
});

const PingParsedJWT = z.object({
  email: z.string(),
  exp: z.coerce.number().transform((u) => new Date(u * 1000)),
});
